/**
 * authValidator.js — Authentication Validation Utilities
 * ─────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for authentication checks.
 *
 * CHANGES:
 *  1. isAuthenticated now also checks token expiry (was never checked before)
 *  2. Added hasValidSession() that combines Redux state + cookie fallback
 *  3. Defensive try/catch on every export (store may not be ready)
 *  4. getUserInfo returns null consistently instead of mixed null/undefined
 */

import moment from 'moment';
import { store } from '../store/configureStore';

/**
 * Check if user has valid authentication info in Redux store.
 * Returns false rather than throwing if store is not ready.
 */
export const isAuthenticated = () => {
  try {
    const authInfo = store.getState().authInfo;

    if (!authInfo || !authInfo.oaAuthTokens || !authInfo.oaAuthTokens.access_token) {
      return false;
    }

    if (!authInfo.userDetails) {
      return false;
    }

    // Also reject if token is known to be expired
    if (isTokenExpired()) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

/**
 * Check if the access token has expired.
 * Returns true (expired) if any data is missing — safe default.
 */
export const isTokenExpired = () => {
  try {
    const authInfo = store.getState().authInfo;

    if (!authInfo || !authInfo.oaAuthTokens) {
      return true;
    }

    const { token_creationTime, expires_in } = authInfo.oaAuthTokens;

    // If no expiration info, let the backend decide
    if (!token_creationTime || !expires_in) {
      return false;
    }

    const expiryTime = moment(token_creationTime).add(expires_in, 'seconds');
    return moment().isAfter(expiryTime);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Combined session check: Redux state OR session cookie.
 * Useful for the App component routing decisions.
 */
export const hasValidSession = () => {
  try {
    if (isAuthenticated()) {
      return true;
    }

    // Fallback: check for session cookies (set by backend)
    const cookies = document.cookie.split(';');
    return cookies.some((cookie) => {
      const trimmed = cookie.trim();
      return trimmed.startsWith('session_token=') ||
             trimmed.startsWith('auth_session=');
    });
  } catch (error) {
    console.error('Error checking session:', error);
    return false;
  }
};

/**
 * Get user details from store.
 * Always returns an object or null — never undefined.
 */
export const getUserInfo = () => {
  try {
    const authInfo = store.getState().authInfo;
    return (authInfo && authInfo.userDetails) ? authInfo.userDetails : null;
  } catch (error) {
    console.error('Error getting user info:', error);
    return null;
  }
};

/**
 * Check if user must reset password on first login.
 */
export const needsPasswordReset = () => {
  try {
    const userInfo = getUserInfo();
    return userInfo !== null && userInfo.isReset === 0;
  } catch (error) {
    return false;
  }
};
